import { State, StateProps } from "./state";
import { Transition, TransitionProps } from "./transition";

interface DependentOperationRule {
  operation: StateProps["operation"];
  baseStateType: StateProps["stateType"];
  dependentStateType: StateProps["stateType"];
  entryEvent: TransitionProps["event"];
}

export class DependentOperationManager {
  private rulesByOperation: Map<
    StateProps["operation"],
    DependentOperationRule
  >;

  constructor(rules: DependentOperationRule[]) {
    this.rulesByOperation = new Map(
      rules.map((rule) => [rule.operation, rule])
    );
  }

  public getRule(operation: StateProps["operation"]) {
    return this.rulesByOperation.get(operation);
  }

  public hasRule(operation: StateProps["operation"]) {
    return this.rulesByOperation.has(operation);
  }

  public getDefaultStateType(operation: StateProps["operation"]) {
    const rule = this.rulesByOperation.get(operation);
    return rule?.baseStateType ?? "EXECUTOR";
  }

  public sortOperationStates(
    operation: StateProps["operation"],
    states: State[]
  ) {
    const rule = this.rulesByOperation.get(operation);
    if (!rule) {
      return [...states].sort((a, b) => a.id - b.id);
    }

    const baseStates = states.filter(
      (state) => state.stateType === rule.baseStateType
    );
    const dependentStates = states
      .filter((state) => state.stateType !== rule.baseStateType)
      .sort((a, b) => a.id - b.id);

    return [...baseStates, ...dependentStates];
  }

  public getBaseStateTransitions(state: State, allStates: State[]) {
    const rule = this.rulesByOperation.get(state.operation);
    if (!rule || state.stateType !== rule.baseStateType) return null;

    const dependentStates = allStates
      .filter(
        (candidate) =>
          candidate.operation === state.operation &&
          candidate.stateType === rule.dependentStateType
      )
      .sort((a, b) => a.id - b.id);

    if (!dependentStates.length) return undefined;

    return dependentStates.map(
      (target) => new Transition({ event: rule.entryEvent, target: target.id })
    );
  }

  public getBaseState(operation: StateProps["operation"], allStates: State[]) {
    const rule = this.rulesByOperation.get(operation);
    if (!rule) return undefined;

    return allStates.find(
      (state) =>
        state.operation === operation && state.stateType === rule.baseStateType
    );
  }

  public getDependentStates(
    operation: StateProps["operation"],
    allStates: State[]
  ) {
    const rule = this.rulesByOperation.get(operation);
    if (!rule) return [];

    return allStates.filter(
      (state) =>
        state.operation === operation &&
        state.stateType === rule.dependentStateType
    );
  }
}
